/**
 * Playground interceptor handlers for Organization Management CRUD operations.
 * Matches API pattern: /v1/web/org-mgmt
 *
 * Special behavior: The primary org is stored as a single object under pg_org.
 * Additional orgs use PlaygroundSessionStore with key pg_orgs.
 */
import { PlaygroundSessionStore, PlaygroundLimitError } from '../PlaygroundSessionStore.js';

const orgsStore = new PlaygroundSessionStore('pg_orgs', 5);
const now = () => new Date().toISOString();

/**
 * Read the primary org from sessionStorage (single object, not array).
 */
function getPrimaryOrg() {
    try {
        const raw = sessionStorage.getItem('pg_org');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/**
 * Write the primary org back to sessionStorage.
 */
function savePrimaryOrg(org) {
    sessionStorage.setItem('pg_org', JSON.stringify(org));
}

/**
 * GET /v1/web/org-mgmt - list all orgs (primary org + any additional orgs).
 */
export function handleGetOrgs(config, match) {
    const primaryOrg = getPrimaryOrg();
    const additionalOrgs = orgsStore.getAll();
    const allOrgs = [];
    if (primaryOrg) {
        allOrgs.push(primaryOrg);
    }
    allOrgs.push(...additionalOrgs);
    return { orgs: allOrgs };
}

/**
 * GET /v1/web/org-mgmt/:id - single org by id.
 */
export function handleGetOrgById(config, match) {
    const id = match[1];
    const primaryOrg = getPrimaryOrg();
    if (primaryOrg && String(primaryOrg.id) === String(id)) {
        return primaryOrg;
    }
    const org = orgsStore.getById(id);
    if (!org) {
        throw new Error('Organization with id ' + id + ' not found');
    }
    return org;
}

/**
 * POST /v1/web/org-mgmt - create new org.
 */
export function handleCreateOrg(config, match) {
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const id = PlaygroundSessionStore.getNextId();
    const newOrg = {
        ...data,
        id,
        orgState: data.orgState || 'ACTIVE',
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    };
    return orgsStore.create(newOrg);
}

/**
 * PUT /v1/web/org-mgmt/:id - update existing org.
 */
export function handleUpdateOrg(config, match) {
    const id = match[1];
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const primaryOrg = getPrimaryOrg();

    if (primaryOrg && String(primaryOrg.id) === String(id)) {
        const updated = {
            ...primaryOrg,
            ...data,
            id,
            lastModifiedDate: now(),
            lastModifiedBy: 'Playground Explorer',
        };
        savePrimaryOrg(updated);
        return updated;
    }

    const updated = {
        ...data,
        id,
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    };
    return orgsStore.update(id, updated);
}

/**
 * PATCH /v1/web/org-mgmt/:id/archive - update orgState to ARCHIVED.
 */
export function handleArchiveOrg(config, match) {
    const id = match[1];
    const primaryOrg = getPrimaryOrg();

    if (primaryOrg && String(primaryOrg.id) === String(id)) {
        const updated = {
            ...primaryOrg,
            orgState: 'ARCHIVED',
            lastModifiedDate: now(),
            lastModifiedBy: 'Playground Explorer',
        };
        savePrimaryOrg(updated);
        return updated;
    }

    const existing = orgsStore.getById(id);
    if (!existing) {
        throw new Error('Organization with id ' + id + ' not found');
    }
    return orgsStore.update(id, {
        ...existing,
        orgState: 'ARCHIVED',
        lastModifiedDate: now(),
        lastModifiedBy: 'Playground Explorer',
    });
}
